import { Person } from '../types';
import type { OverviewFieldsPatch } from './api';
import { peopleApi } from './api';
import { profileUpdateApi } from './profileUpdateApi';
import type {
  ExperienceEntryInput,
  SkillInput,
  DomainInput,
  EducationInput,
  CertificationInput,
  LanguageInput,
} from '../types/candidateDetails';

/**
 * Common interface for profile mutations consumed by the Unified Person Modal
 * and its nested editors. Implementations route to either the admin/hr endpoints,
 * the magic-link self-service endpoints, or the authenticated self-service endpoints.
 */
export interface ProfileMutationsApi {
  patchOverviewFields(id: number, payload: OverviewFieldsPatch): Promise<Person>;
  replaceExperience(id: number, entries: ExperienceEntryInput[]): Promise<Person>;
  replaceSkills(id: number, entries: SkillInput[]): Promise<Person>;
  replaceDomains(id: number, entries: DomainInput[]): Promise<Person>;
  replaceEducation(id: number, entries: EducationInput[]): Promise<Person>;
  replaceCertifications(id: number, entries: CertificationInput[]): Promise<Person>;
  replaceLanguages(id: number, entries: LanguageInput[]): Promise<Person>;
}

/**
 * Standard implementation for Admin/HR users. Routes to the existing
 * /api/people/:id/* endpoints via peopleApi.
 */
export const adminProfileApi: ProfileMutationsApi = {
  patchOverviewFields: (id, payload) => peopleApi.patchOverviewFields(id, payload),
  replaceExperience: (id, entries) => peopleApi.replaceExperience(id, entries),
  replaceSkills: (id, entries) => peopleApi.replaceSkills(id, entries),
  replaceDomains: (id, entries) => peopleApi.replaceDomains(id, entries),
  replaceEducation: (id, entries) => peopleApi.replaceEducation(id, entries),
  replaceCertifications: (id, entries) => peopleApi.replaceCertifications(id, entries),
  replaceLanguages: (id, entries) => peopleApi.replaceLanguages(id, entries),
};

/**
 * Implementation for external candidates using a Magic Link.
 * Routes to the /api/profile-update/* endpoints (Phase 3F).
 */
export const selfMagicLinkApi = (token: string, jwt: string): ProfileMutationsApi => ({
  patchOverviewFields: async (_id, payload) => {
    const rest: OverviewFieldsPatch = { ...payload };
    delete rest.contact;
    return profileUpdateApi.patchOverviewFields(token, jwt, rest);
  },
  replaceExperience: async (_id, entries) => profileUpdateApi.replaceExperience(token, jwt, entries),
  replaceSkills: async (_id, entries) => profileUpdateApi.replaceSkills(token, jwt, entries),
  replaceDomains: async (_id, entries) => profileUpdateApi.replaceDomains(token, jwt, entries),
  replaceEducation: async (_id, entries) => profileUpdateApi.replaceEducation(token, jwt, entries),
  replaceCertifications: async (_id, entries) => profileUpdateApi.replaceCertifications(token, jwt, entries),
  replaceLanguages: async (_id, entries) => profileUpdateApi.replaceLanguages(token, jwt, entries),
});

/**
 * Implementation for authenticated employees updating their own profile.
 * Routes to the /api/profile-update/me/* endpoints using their session cookie
 * (to be implemented in Phase 3F).
 */
export const selfSessionApi: ProfileMutationsApi = {
  patchOverviewFields: async () => { throw new Error('Not implemented: selfSessionApi.patchOverviewFields'); },
  replaceExperience: async () => { throw new Error('Not implemented: selfSessionApi.replaceExperience'); },
  replaceSkills: async () => { throw new Error('Not implemented: selfSessionApi.replaceSkills'); },
  replaceDomains: async () => { throw new Error('Not implemented: selfSessionApi.replaceDomains'); },
  replaceEducation: async () => { throw new Error('Not implemented: selfSessionApi.replaceEducation'); },
  replaceCertifications: async () => { throw new Error('Not implemented: selfSessionApi.replaceCertifications'); },
  replaceLanguages: async () => { throw new Error('Not implemented: selfSessionApi.replaceLanguages'); },
};
